import React, { useCallback, useEffect, useMemo, useState } from "react";
import { WizardFilesSectionProps, StagedFile } from "./definitions";
import { useWizardFileOperations } from "./useWizardFileOperations";
import styles from "./style.module.scss";
import {
  useListDocumentsQuery,
  useDeleteDocumentsMutation,
  useUpdateDocumentsMutation,
  useLazyDownloadUrlQuery,
} from "../documents-table/endpoints";
import InputFilesModal from "components/input-files-modal";
import CreateDocumentModal from "components/create-document-modal";
import { useNotifications } from "features/notifications";
import {
  useRegisterDocumentMutation,
  useUploadUrlMutation,
} from "components/create-audit-form/endpoints";
import { useAppSelector } from "app/store";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import CommonDeleteModal from "components/common-delete-modal";
import {
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  Header,
  SpaceBetween,
  Spinner,
  Table,
} from "@amzn/awsui-components-react";
import {
  getWizardDeleteAction,
  getWizardDeleteInfo,
  getWizardVersionedDeleteMessage,
} from "./utils";
import { createWizardColumnDefinitions } from "./column-definitions";
import { FileMetadata } from "components/input-files/definitions";
import { CreateDocumentFields } from "models/schemas";
import { cap } from "utils/date-format";
import { getCategoryById } from "utils/gaiaobject-utils";
import { DocumentCollection } from "../documents-table/version-utils";
import {
  filterDisplayFiles,
  cascadeDeleteVersionedFiles,
} from "./version-utils";
import { GaiaConstants } from "utils";

const WizardFilesSection: React.FC<WizardFilesSectionProps> = (
  props: WizardFilesSectionProps
) => {
  const {
    parentId,
    gaiaObjectId,
    category,
    stagedFiles,
    onStagedFilesChange,
    isCreateMode,
  } = props;

  const teamName: string = useAppSelector((state) => state.audits.teamName);
  const currentUser: string = useAppSelector((state) => state.auth.user.alias);
  const gaiaObjectType = getCategoryById(gaiaObjectId);
  const uiContent: any = useGaiaObjectUiContent(gaiaObjectType);
  const { addNotification } = useNotifications();
  const [uploadUrlCall] = useUploadUrlMutation();
  const [registerDocCall] = useRegisterDocumentMutation();
  const [deleteDocCall] = useDeleteDocumentsMutation();
  const [updateDocCall] = useUpdateDocumentsMutation();
  const [lazyDownloadUrlCall] = useLazyDownloadUrlQuery();

  const [selectedItems, setSelectedItems] = useState<StagedFile[]>([]);
  const [addDocModalVisible, setAddDocModalVisible] = useState(false);
  const [deleteDocModalIsVisible, setDeleteDocModalIsVisible] = useState(false);
  const [docToRename, setDocToRename] = useState<StagedFile>();
  const [singleDoc, setSingleDoc] = useState<StagedFile>();
  const [fileName, setFileName] = useState<string>("");
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [documentCollection, setDocumentCollection] =
    useState<DocumentCollection | null>(null);

  // Initialize wizard file operations hook
  const {
    downloadSingleDoc,
    downloadDocs,
    addStagedFile,
    addStagedLink,
    addMultipleStagedFiles,
    removeStagedFile,
    updateStagedFileName,
    restoreStagedFile,
    loadExistingFiles,
  } = useWizardFileOperations({
    uploadUrlCall,
    registerDocCall,
    deleteDocCall,
    updateDocCall,
    lazyDownloadUrlCall,
    addNotification,
    teamName,
    currentUser,
    parentId,
    gaiaObjectId,
    category,
    stagedFiles,
    onStagedFilesChange,
    isCreateMode,
    documentCollection,
    onDocumentCollectionChange: setDocumentCollection,
  });

  // Load existing documents for edit mode
  const {
    data: documentsData,
    isLoading,
    isError,
  } = useListDocumentsQuery(
    { parentId: parentId || "" },
    {
      refetchOnMountOrArgChange: true,
      skip: isCreateMode || !parentId,
    }
  );

  useEffect(() => {
    if (!isCreateMode && documentsData?.documents && stagedFiles.length === 0) {
      loadExistingFiles(documentsData.documents);
    }
  }, [documentsData, isCreateMode, loadExistingFiles, stagedFiles.length]);

  const handleFilesActionsButtonClick = useCallback(
    (event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
      const action = event.detail.id;
      if (action === "dl") {
        downloadDocs(selectedItems);
      } else if (action === "rm") {
        setDeleteDocModalIsVisible(true);
      }
    },
    [selectedItems, downloadDocs]
  );

  const handleUploadButtonClick = useCallback(
    (event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
      const action = event.detail.id;
      if (action === "file") {
        setIsFileUpload(true);
        setAddDocModalVisible(true);
      } else if (action === "link") {
        setIsFileUpload(false);
        setAddDocModalVisible(true);
      }
    },
    []
  );

  const handleItemActionClick = useCallback(
    (
      event: CustomEvent<ButtonDropdownProps.ItemClickDetails>,
      item: StagedFile
    ) => {
      const action = event.detail.id;
      if (action === "download") {
        downloadSingleDoc(item);
      } else if (action === "rename") {
        setDocToRename(item);
        setFileName(item.fileName);
      } else if (action === "delete") {
        setSingleDoc(item);
        setDeleteDocModalIsVisible(true);
      } else if (action === "restore") {
        restoreStagedFile(item.id);
      }
    },
    [downloadSingleDoc, restoreStagedFile]
  );

  const handleDocDownload = useCallback(
    (stagedFile: StagedFile) => {
      downloadSingleDoc(stagedFile);
    },
    [downloadSingleDoc]
  );

  const handleChangeDocFileName = useCallback(
    (stagedFile: StagedFile) => {
      updateStagedFileName(stagedFile.id, fileName);
      setDocToRename(undefined);
      setFileName("");
    },
    [updateStagedFileName, fileName]
  );

  const onCreateDoc = useCallback(
    async (
      doc: CreateDocumentFields,
      file: File,
      link: string,
      fileName: string,
      callback: () => void
    ) => {
      if (file) {
        addStagedFile(file, fileName, doc.documentType);
      } else {
        addStagedLink(link, fileName, doc.documentType);
      }
      callback();
      addNotification({
        type: "success",
        header: file ? "File added." : "Link added.",
        dismissible: true,
        timeout: 5000,
      });
    },
    [addStagedFile, addStagedLink, addNotification]
  );

  const onMultipleFileUpload = useCallback(
    async (files: FileMetadata[], callback: () => void) => {
      addMultipleStagedFiles(files);
      callback();
      addNotification({
        type: "success",
        header: `${files.length} file${files.length > 1 ? "s" : ""} added.`,
        dismissible: true,
        timeout: 5000,
      });
    },
    [addMultipleStagedFiles, addNotification]
  );

  // Handle cascade delete for versioned files
  const deleteDocs = useCallback(
    (docsToDelete: StagedFile[]) => {
      const updatedFiles = cascadeDeleteVersionedFiles(
        docsToDelete,
        documentCollection,
        stagedFiles
      );

      onStagedFilesChange(updatedFiles);
      setSelectedItems([]);
      setSingleDoc(undefined);
      setDeleteDocModalIsVisible(false);
      addNotification({
        type: "success",
        header: getWizardDeleteInfo(docsToDelete, " successfully", true),
        dismissible: true,
        timeout: 5000,
      });
    },
    [onStagedFilesChange, addNotification, documentCollection, stagedFiles]
  );

  const getFileNames = useCallback(
    (singleDoc: StagedFile | undefined, selectedItems: StagedFile[]) => {
      if (!singleDoc) {
        return (
          selectedItems?.map((d: StagedFile) => d.fileName).join(", ") || ""
        );
      }
      return singleDoc?.fileName;
    },
    []
  );

  // Create column definitions for the wizard files table
  const columnProps = {
    docToRename,
    fileName,
    setFileName,
    setDocToRename,
    changeDocFileName: handleChangeDocFileName,
    handleDocDownload,
    downloadSingleDoc,
    handleItemActionClick,
    parentId: parentId || "",
    category,
    uiContent,
    isCreateMode,
  };

  const columnDefinitions = createWizardColumnDefinitions(columnProps);

  // Filter out files marked for deletion and older versions for display
  const displayFiles = filterDisplayFiles(stagedFiles);
  const filesNum = displayFiles.length;

  if (isLoading && !isCreateMode) {
    return (
      <Box textAlign="center">
        <Spinner size="large" />
      </Box>
    );
  }

  if (isError && !isCreateMode) {
    return (
      <Box textAlign="center">
        <b className={styles.errorInfo}>
          Error loading files... Please contact the Gaia Team for assistance.
        </b>
      </Box>
    );
  }

  return (
    <div
      className={styles.wizardFilesSection}
      data-testid="wizard-files-section"
    >
      <Table
        onSelectionChange={({ detail }) => {
          setSelectedItems(detail.selectedItems);
        }}
        selectedItems={selectedItems}
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          allItemsSelectionLabel: ({ selectedItems }) =>
            `${selectedItems.length} ${
              selectedItems.length === 1 ? "item" : "items"
            } selected`,
          itemSelectionLabel: ({ selectedItems }, item: any) =>
            (item as any).id,
        }}
        resizableColumns
        columnDefinitions={columnDefinitions}
        enableKeyboardNavigation
        items={displayFiles}
        loadingText="Loading resources"
        selectionType="single"
        trackBy="id"
        empty={
          <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>No files</b>
            </SpaceBetween>
          </Box>
        }
        header={
          <Header
            actions={
              <SpaceBetween size={"s"} direction="horizontal">
                <ButtonDropdown
                  data-testid="wizard-files-dropdown"
                  items={[
                    {
                      id: "dl",
                      text: "Download",
                      disabled: !selectedItems.length || isCreateMode,
                      disabledReason: isCreateMode
                        ? "Downloads not available for new files"
                        : "No document is selected.",
                    },
                    {
                      id: "rm",
                      text: "Remove",
                      disabled: !selectedItems.length,
                      disabledReason: "No document is selected.",
                    },
                  ]}
                  onItemClick={handleFilesActionsButtonClick}
                  expandToViewport
                >
                  Files actions
                </ButtonDropdown>
                <ButtonDropdown
                  variant="primary"
                  items={[
                    {
                      id: "file",
                      text: "Upload files",
                    },
                    {
                      id: "link",
                      text: "Add link",
                    },
                  ]}
                  onItemClick={handleUploadButtonClick}
                  expandToViewport
                >
                  Add
                </ButtonDropdown>
              </SpaceBetween>
            }
            description="Files will uploaded when the form is saved."
          >
            Files ({filesNum})
          </Header>
        }
      />
      {addDocModalVisible &&
        (isFileUpload ? (
          <InputFilesModal
            onUpload={onMultipleFileUpload}
            visible={addDocModalVisible}
            setVisible={setAddDocModalVisible}
            description={GaiaConstants.DOCUMENT_STORE_DISCLAIMER}
            enableRenaming={false}
            enableDocumentTypes={true}
            requireDocumentTypes={true}
            defaultDocumentType={cap(props.category)}
            gaiaObjectType={gaiaObjectType}
            documentCollection={documentCollection}
            parentId={parentId}
            versioningEnabled={!isCreateMode}
            stagedFiles={stagedFiles}
            dragAndDropEnabled={true}
          />
        ) : (
          <CreateDocumentModal
            onCreate={onCreateDoc}
            visible={addDocModalVisible}
            setVisible={setAddDocModalVisible}
            isFileUpload={isFileUpload}
            title={isFileUpload ? "Upload file" : "Add link"}
            defaultDocumentType={cap(props.category)}
            gaiaObjectType={gaiaObjectType}
          />
        ))}
      <CommonDeleteModal
        setVisible={setDeleteDocModalIsVisible}
        visible={deleteDocModalIsVisible}
        onDelete={() => deleteDocs(singleDoc ? [singleDoc] : selectedItems)}
        items={singleDoc ? singleDoc : selectedItems}
        title={getWizardDeleteInfo(
          singleDoc ? [singleDoc] : selectedItems,
          "?"
        )}
        message={getWizardVersionedDeleteMessage(
          singleDoc ? [singleDoc] : selectedItems,
          documentCollection,
          getFileNames(singleDoc, selectedItems)
        )}
        deleteButtonText={getWizardDeleteAction(
          singleDoc ? [singleDoc] : selectedItems
        )}
      />
    </div>
  );
};

export default WizardFilesSection;
